import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { database } from '../../firebase';
import { ref, get, update } from 'firebase/database';
import './PublicOnboardingPages.css';

const PublicFormCollection = () => {
  useUiLanguage();
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [taskData, setTaskData] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [companyInfo, setCompanyInfo] = useState(null);

    useEffect(() => {
        const fetchForm = async () => {
            try {
                if (!token) throw new Error('No token provided');

                // Decode token: companyId:hireId:category:taskId
                const decoded = atob(token);
                const parts = decoded.split(':');

                if (parts.length < 4) throw new Error('Invalid link format');

                const [companyId, hireId, category, taskId] = parts;

                // Path to specific task
                const taskPath = `companies/${companyId}/onboarding/${hireId}/tasks/${category}/workflow/tasks/${taskId}`;

                // Fetch Task
                const taskRef = ref(database, taskPath);
                const snapshot = await get(taskRef);

                if (snapshot.exists()) {
                    const task = snapshot.val();

                    // Optional: check status
                    // if (task.status === 'completed') ...

                    setTaskData({
                        ...task,
                        path: taskPath
                    });

                    // Fetch Company Info for branding
                    const companyRef = ref(database, `companies/${companyId}`);
                    const compSnap = await get(companyRef);
                    if (compSnap.exists()) {
                        setCompanyInfo(compSnap.val());
                    }
                } else {
                    setError(uiText('Form not found or link has expired.'));
                }
            } catch (err) {
                console.error(err);
                setError(uiText('Invalid or expired link.'));
            } finally {
                setLoading(false);
            }
        };

        fetchForm();
    }, [token]);

    const handleInputChange = (fieldId, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldId]: value
        }));
    };

    const handleFileUpload = async (fieldId, file) => {
        setFormData(prev => ({
            ...prev,
            [fieldId]: `File: ${file.name} (Upload not fully implemented in minimal version)`
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!taskData) return;

        // Validate required fields
        const missing = taskData.formConfig?.fields?.filter(f => f.required && !formData[f.id]);
        if (missing?.length > 0) {
            alert(uiText('Please fill in required fields: {{fields}}', { fields: missing.map(f => f.label).join(', ') }));
            return;
        }

        try {
            await update(ref(database, taskData.path), {
                status: 'completed',
                formResponse: formData,
                completedAt: new Date().toISOString()
            });
            setSubmitted(true);
        } catch (err) {
            alert('Failed to submit form: ' + err.message);
        }
    };

    if (loading) return (
        <div className="public-page">
            <div className="public-page-shell">
                <div className="public-card public-status-card">
                    <div className="public-status-icon loading">
                        <i className="fa-solid fa-spinner fa-spin"></i>
                    </div>
                    <h2 className="public-title">{uiText("Loading Form")}</h2>
                    <p className="public-subtitle">{uiText("Preparing your secure form request.")}</p>
                </div>
            </div>
        </div>
    );
    if (error) return (
        <div className="public-page">
            <div className="public-page-shell">
                <div className="public-card public-status-card">
                    <div className="public-status-icon error">
                        <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <h2 className="public-title">{uiText("Link Error")}</h2>
                    <p className="public-subtitle">{uiText(error)}</p>
                </div>
            </div>
        </div>
    );
    if (submitted) return (
        <div className="public-page">
            <div className="public-page-shell">
                <div className="public-card public-status-card">
                    <div className="public-status-icon success">
                        <i className="fa-solid fa-circle-check"></i>
                    </div>
                    <h2 className="public-title">{uiText("Thank You!")}</h2>
                    <p className="public-subtitle">{uiText("Your information has been successfully submitted.")}</p>
                </div>
            </div>
        </div>
    );

    const { formConfig } = taskData || {};
    if (!formConfig) return (
        <div className="public-page">
            <div className="public-page-shell">
                <div className="public-card public-status-card">
                    <div className="public-status-icon error">
                        <i className="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <h2 className="public-title">{uiText("Invalid Form")}</h2>
                    <p className="public-subtitle">{uiText("The requested form configuration is unavailable.")}</p>
                </div>
            </div>
        </div>
    );

    return (
        <div className="public-page">
            <div className="public-page-shell">
                <div className="public-card">
                <div className="form-header">
                        {companyInfo?.companyName && (
                            <div className="public-company">
                                <i className="fa-solid fa-building"></i>
                                {companyInfo.companyName}
                            </div>
                        )}
                        <h1 className="public-title">{formConfig.title}</h1>
                        {formConfig.description && <p className="public-subtitle">{formConfig.description}</p>}
                </div>

                    <form className="public-form" onSubmit={handleSubmit}>
                    {formConfig.fields.map(field => (
                            <div key={field.id} className="public-form-group">
                                <label className="public-form-label">
                                {uiText(field.label)}
                                    {field.required && <span className="public-required">*</span>}
                            </label>

                            {field.type === 'textarea' ? (
                                <textarea
                                        className="public-textarea"
                                    value={formData[field.id] || ''}
                                    onChange={e => handleInputChange(field.id, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                    rows={4}
                                />
                            ) : field.type === 'select' ? (
                                <select
                                        className="public-select"
                                    value={formData[field.id] || ''}
                                    onChange={e => handleInputChange(field.id, e.target.value)}
                                    required={field.required}
                                >
                                    <option value="">{uiText("Select an option")}</option>
                                    {field.options?.map((opt, i) => (
                                        <option key={i} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            ) : field.type === 'checkbox' ? (
                                    <label className="public-checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={!!formData[field.id]}
                                        onChange={e => handleInputChange(field.id, e.target.checked)}
                                    />
                                    <span>{field.checkboxLabel || field.label}</span>
                                </label>
                            ) : field.type === 'file' ? (
                                <input
                                        className="public-file-input"
                                    type="file"
                                    onChange={e => handleFileUpload(field.id, e.target.files[0])}
                                    accept={field.acceptedTypes}
                                    required={field.required}
                                />
                            ) : (
                                <input
                                        className="public-input"
                                    type={field.type}
                                    value={formData[field.id] || ''}
                                    onChange={e => handleInputChange(field.id, e.target.value)}
                                    placeholder={field.placeholder}
                                    required={field.required}
                                />
                            )}
                        </div>
                    ))}

                        <button type="submit" className="public-submit">{uiText("Submit Information")}</button>
                </form>
            </div>
            </div>
        </div>
    );
};

export default PublicFormCollection;
